import {useDispatch, useSelector} from 'react-redux';

import Banner from '@/components/Banner/Banner';
import {AppButton} from '@/components/Button';
import PromotedBadge from '@/components/PromotedBadge/PromotedBadge';
import {PUSH_NOTIFICATIONS_PROMPT_TYPES} from '@/consts/pushNotifications/pushNotificationsPromptTypes';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import amplitudeAutoDeliveryDcxTracking from '@/services/analytics/amplitude/amplitudeAutoDeliveryDcxTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import appContent from '@/services/content/appContent.typed';
import {schedulePushPromptIfNeeded} from '@/services/pushNotification/pushNotificationPromptService';
import {selectIsAem, selectIsMobileLayout} from '@/state/selectors/global';
import {showAutoDeliveryStatusNoneDetailsPopup} from '@/state/slices/modalSlice';

import AutoDeliveryDcxWidgetContainer from './components/AutoDeliveryDcxWidgetContainer';
import AutoDeliveryDcxWidgetHeader from './components/AutoDeliveryDcxWidgetHeader';

interface AutoDeliveryDcxStatusNoneProps {
    className?: string;
}

const AutoDeliveryDcxStatusNone = ({className}: AutoDeliveryDcxStatusNoneProps) => {
    const dispatch = useDispatch();
    const isMobileLayout = useSelector(selectIsMobileLayout);
    const content = appContent.getAutoDeliveryContent()?.dashboardContent?.userOnboardDashboard;
    const {title, description, promotion, images, videos, button} = content?.banner || {};
    const isAem = useSelector(selectIsAem);
    const isBannerDataExist = images?.length || videos?.length || title || promotion || description;
    const videoPath = videos?.[0]?.path;
    const ariaLabel = button?.alt;

    const onBannerClick = () => {
        schedulePushPromptIfNeeded(PUSH_NOTIFICATIONS_PROMPT_TYPES.AUTODELIVERY_DASHBOARD_CLICK);
        amplitudeAutoDeliveryDcxTracking.trackAutoDeliveryDcxBannerClick();
        googleAnalyticsService.trackAutoDeliveryBannerClick();
        dispatch(showAutoDeliveryStatusNoneDetailsPopup());
    };

    if (!isBannerDataExist && !isAem) return null;

    return (
        <AutoDeliveryDcxWidgetContainer className={className}>
            {isMobileLayout && (
                <>
                    <AutoDeliveryDcxWidgetHeader showManageButton={false} />
                    <Banner
                        className='mt-4 h-60 overflow-hidden rounded-xl bg-primary text-start'
                        imageMedia={images}
                        videoPath={videoPath}
                        isGradient
                        gradient='linear-gradient(180deg, rgba(52, 48, 61, 0) 39.99%, #1C1A21 100%)'
                        aria-label={ariaLabel}
                        onClick={onBannerClick}
                    >
                        <AutoDeliveryDcxStatusNoneContent
                            title={title}
                            promotion={promotion}
                            description={description}
                        />
                    </Banner>
                </>
            )}
            {!isMobileLayout && (
                <div className='flex w-full items-center rounded-xl bg-primary-dark'>
                    <AutoDeliveryDcxStatusNoneContent
                        title={title}
                        promotion={promotion}
                        description={description}
                        buttonText={button?.text}
                        aria-label={ariaLabel}
                        onClick={onBannerClick}
                    />
                    <Banner
                        className='h-100 w-1/2 overflow-hidden rounded-r-xl'
                        imageMedia={images}
                        videoPath={videoPath}
                        isGradient={false}
                    />
                </div>
            )}
        </AutoDeliveryDcxWidgetContainer>
    );
};

interface AutoDeliveryDcxStatusNoneContentProps {
    title?: string;
    promotion?: string;
    description?: string;
    buttonText?: string;
    'aria-label'?: string;
    onClick?: () => void;
}

const AutoDeliveryDcxStatusNoneContent = (props: AutoDeliveryDcxStatusNoneContentProps) => {
    const {title, promotion, description, buttonText, 'aria-label': ariaLabel, onClick} = props;
    const isMobile = useIsMobileLayout();
    const Heading = isMobile ? 'h3' : 'h2';

    return (
        <div className='relative flex h-full flex-col justify-end gap-2 p-4 ml:w-1/2 ml:gap-3.5 ml:p-16'>
            <PromotedBadge
                className='max-w-full whitespace-nowrap [&>*]:overflow-hidden [&>*]:text-ellipsis'
                badgeText={promotion}
            />
            <Heading className='mb-0 overflow-hidden text-ellipsis whitespace-nowrap text-24 text-white ml:text-44 ml:leading-[56px]'>
                {title}
            </Heading>
            {description && (
                <p className='line-clamp-3 whitespace-pre-line text-16 leading-[20px] text-white ml:text-18 ml:leading-[24px]'>
                    {description}
                </p>
            )}
            {buttonText && (
                <div>
                    <AppButton primary dark className='mt-2.5 w-full' onClick={onClick} aria-label={ariaLabel}>
                        {buttonText}
                    </AppButton>
                </div>
            )}
        </div>
    );
};

export default AutoDeliveryDcxStatusNone;
